import { useEffect, useRef, useState } from 'react';
import { canIdentifyBy, IdentifierKey } from '@/features/people';
import { useObjectAttributes, type ObjectAttributesEditorState } from '@/features/objects';
import { useAnalytics } from '@/services/analytics';
import { useCanCreateObjectRelationships } from '@/services/permissions/hooks';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { ObjectRecord } from '@/services/db/object/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import {
  buildNavSteps,
  isAdvanceBlocked,
  stepAfter,
  stepBefore,
  toRelationshipStep,
  type NavStep,
  type RelationshipStep,
} from './steps';
import { useObjectPicker, type ObjectPicker } from './use-object-picker';
import { usePeoplePicker, type PeoplePicker } from './use-people-picker';
import { useRelationshipSubmit } from './use-relationship-submit';

export type AddRelationshipsModalData = {
  objectType: ObjectType;
  workspaceId: number;
  /** Skip the object-picking step and relate people to this object. */
  preselectedObject?: ObjectRecord;
  /** Analytics source, e.g. 'Object List' or 'Object Profile'. */
  source?: string;
};

export type AddRelationshipsWizard = {
  step: RelationshipStep;
  /** Takes the tablist's reported value, which is an unvalidated string. */
  setStep: (step: string | null) => void;
  steps: NavStep[];
  people: PeoplePicker;
  objects: ObjectPicker;
  editor: ObjectAttributesEditorState;
  isLoading: boolean;
  isSubmitting: boolean;
  canSelect: boolean;
  canIdentifyByPhone: boolean;
  peoplePlural: string;
  peopleSingular: string;
  /** Null on the first step, where the footer cancels instead. */
  previousStep: RelationshipStep | null;
  isAdvanceDisabled: boolean;
  /** Moves to the next step, or submits from the last one. */
  advance: () => void;
};

type WizardOptions = {
  data: AddRelationshipsModalData;
  onConfirm: () => void;
};

/** Fires the open event once, however often the analytics binding changes. */
function useTrackOpened(source: string) {
  const { track } = useAnalytics();
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    void track('objects', 'relationshipsManualAddStarted', { Source: source });
  }, [track, source]);
}

/**
 * Everything the relationships wizard holds across its steps. A tab panel
 * unmounts when the reader steps away, so both picker lists, the attribute
 * editor, and the step itself live here.
 */
export function useAddRelationshipsWizard({ data, onConfirm }: WizardOptions): AddRelationshipsWizard {
  const taxonomy = useProductTaxonomy();
  const canSelect = useCanCreateObjectRelationships();
  const environment = useEnvironmentOptional(data.workspaceId);
  const [step, setStepState] = useState<RelationshipStep>('people');
  const people = usePeoplePicker(data.workspaceId);
  const objects = useObjectPicker({
    workspaceId: data.workspaceId,
    objectTypeId: data.objectType.id,
    preselected: data.preselectedObject,
  });
  // Relationship attributes are plain key/value pairs: the workspace reserves
  // no names among them, so a row called `object_id` is just a row.
  const editor = useObjectAttributes({ initialAttrs: [], isNew: true, hasReservedNames: false });
  const source = data.source ?? 'Object List';
  useTrackOpened(source);
  const { submit, isSubmitting } = useRelationshipSubmit({
    workspaceId: data.workspaceId,
    objectType: data.objectType,
    source,
    people: people.selected,
    objects: objects.selected,
    editor,
    onConfirm,
  });

  const isLoading = people.isFetching || objects.isFetching || isSubmitting;
  const steps = buildNavSteps({
    step,
    hasPreselectedObject: data.preselectedObject != null,
    isLoading,
    peopleCount: people.selected.length,
    objectCount: objects.selected.length,
    objectTypeName: data.objectType.name,
    peopleTitlePlural: taxonomy.term('people.titlePlural'),
    peoplePlural: taxonomy.term('people.plural'),
  });

  return {
    step,
    setStep: (next) => {
      const resolved = toRelationshipStep(next);
      if (resolved != null) setStepState(resolved);
    },
    steps,
    people,
    objects,
    editor,
    isLoading,
    isSubmitting,
    canSelect,
    canIdentifyByPhone: canIdentifyBy(IdentifierKey.Phone, environment.data?.environment?.identifiers),
    peoplePlural: taxonomy.term('people.plural'),
    peopleSingular: taxonomy.term('people.singular'),
    previousStep: stepBefore(steps, step),
    isAdvanceDisabled: isAdvanceBlocked(step, people.selected.length, objects.selected.length),
    advance: () => {
      const next = stepAfter(steps, step);
      if (next == null) {
        submit();
        return;
      }
      setStepState(next);
    },
  };
}
